import Chip from '@mui/material/Chip';
import type { Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';

/**
 * The five chip tones of the mockups: Active (success), Paused (neutral), To set up (warning),
 * Suspended (danger), informative (info). Features map their domain states to a tone (C3 R12).
 */
export type StatusTone = 'success' | 'neutral' | 'warning' | 'danger' | 'info';

/** Props of {@link StatusChip}. */
export interface StatusChipProps {
  readonly tone: StatusTone;
  /** Visible label, already translated by the screen (I12): colour is never the only cue. */
  readonly children: string;
}

function toneColors(theme: Theme, tone: StatusTone): { color: string; backgroundColor: string } {
  const { fx } = theme;
  switch (tone) {
    case 'success':
      return { color: fx.success, backgroundColor: fx.successBg };
    case 'neutral':
      return { color: fx.textMuted, backgroundColor: fx.neutralBg };
    case 'warning':
      return { color: fx.warningText, backgroundColor: fx.warningBg };
    case 'danger':
      return { color: fx.danger, backgroundColor: fx.dangerBg };
    case 'info':
      return { color: fx.info, backgroundColor: fx.infoBg };
  }
}

/**
 * Pill-shaped state label (mockups Main, AccueilBloque, AccueilIncomplet, GestionCanaux).
 *
 * @example <StatusChip tone="success">{t('home.channels.copyState.active')}</StatusChip>
 */
export function StatusChip({ tone, children }: StatusChipProps): ReactElement {
  return (
    <Chip
      size="small"
      label={children}
      data-tone={tone}
      sx={(theme) => ({
        ...toneColors(theme, tone),
        flexShrink: 0,
        // R7: never wider than its row; MUI's label already ends a long text with an ellipsis
        // while the whole label stays in the DOM (accessible name complete).
        maxWidth: '100%',
        '& .MuiChip-label': { px: 1 },
      })}
    />
  );
}
